import React from 'react';
import { css, useTheme } from '@emotion/react';
import { DownOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import styled from 'types/theme.types';

const EXPAND_ICON_CLASS_NAME = 'ant-table-row-expand-icon ant-table-row-expand-icon-spaced';

// antd sizes the icon to the glyph, which leaves a target under the 24px minimum. The padding grows the
// clickable area, the margin keeps a misclick away from the row title.
const GLYPH_SIZE = 14;
const TARGET_PADDING = 6;
const TITLE_GAP = 12;

export const EXPAND_ICON_WIDTH = GLYPH_SIZE + TARGET_PADDING * 2 + TITLE_GAP;

/**
 * The icon's own box, without the title gap. `table.tsx` gives the row-indent span this height because
 * antd ships it at `height: 1px`, and a float only displaces content it overlaps vertically — a 1px
 * float misses the vertically-centred row content entirely. Indentation only ever appeared because
 * this icon was the thing doing the displacing, so a row without one has to do it itself.
 */
export const EXPAND_ICON_BOX = GLYPH_SIZE + TARGET_PADDING * 2;

/* For nested rows antd passes the record's children instead of a boolean, and an empty array
   of children must not count as expandable. */
export const renderTableExpandIcon = <T,>({
  expanded,
  onExpand,
  record,
  expandable
}: {
  expanded: boolean;
  record: T;
  expandable: boolean | T[];
  onExpand: (record: T, event: React.MouseEvent<HTMLElement>) => void;
}): React.ReactNode =>
  (Array.isArray(expandable) ? !isEmpty(expandable) : expandable) ? (
    <StyledExpandIcon
      className={EXPAND_ICON_CLASS_NAME}
      isExpanded={expanded}
      onClick={e => {
        e.stopPropagation();
        onExpand(record, e);
      }}
      aria-label={expanded ? 'Collapse row' : 'Expand row'}
      aria-expanded={expanded}
    />
  ) : (
    <ExpandIconSlot />
  );

/**
 * A row with nothing to expand reserves no width in the new design. Legacy keeps the spacer: its
 * switch-off render is byte-identical by contract (D7.7), and removing it there would move every
 * un-expandable row's title. APO-484 deletes the legacy half with the rest of the branches.
 */
const ExpandIconSlot: React.FC = () =>
  useTheme().isNewDesign ? null : <ExpandIconPlaceholder className={EXPAND_ICON_CLASS_NAME} aria-hidden />;

// antd targets the icon with two classes, so a single emotion class loses on specificity.
const targetBox = `
  &&& {
    box-sizing: content-box;
    width: ${GLYPH_SIZE}px;
    height: ${GLYPH_SIZE}px;
    padding: ${TARGET_PADDING}px;
    margin-inline-end: ${TITLE_GAP}px;
    margin-top: 0;
    line-height: 1;
  }
`;

const StyledExpandIcon = styled(DownOutlined)<{ isExpanded: boolean }>`
  ${targetBox};

  &&& {
    visibility: visible;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 200ms ease-in;

    /* The branch sits where the legacy declaration was: the parity gate is byte-exact, so moving a
       declaration fails it even when nothing renders differently. */
    ${legacyOnly(
      theme => css`
        color: ${theme.iconGrayFill};
      `
    )}

    ${newDesignOnly(css`
      color: var(--component-table-control-icon);
    `)}

    transform: ${({ isExpanded }) => (isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)')};

    ${legacyOnly(
      theme => css`
        &:hover {
          color: ${theme.primaryBlack};
        }
      `
    )}

    ${newDesignOnly(css`
      &:hover {
        color: var(--component-table-cell-text-strong);
      }
    `)}
  }
`;

const ExpandIconPlaceholder = styled.div`
  ${targetBox};
`;
